import { useState } from "react";
import {
  Box,
  Chip,
  Collapse,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import {
  ExpandLess,
  ExpandMore,
  SearchOutlined,
  TravelExploreOutlined,
} from "@mui/icons-material";
import { format } from "date-fns";

import NoContentCard from "src/reusable/Sidebar/NoContentCard";
import { AgentBadge } from "./SourceClassificationChip";
import {
  groupSearchedByCollection,
  type SearchedCollectionGroup,
} from "./searchedTierGrouping";
import type {
  ProvenanceSearchedEntry,
  ProvenanceSource,
} from "../../types/provenance";

/** Format a provenance timestamp ("MMM d, yyyy p"), or null if unparseable. */
const formatTimestamp = (ts?: string | null): string | null => {
  if (!ts) return null;
  const d = new Date(ts);
  return Number.isNaN(d.getTime()) ? null : format(d, "MMM d, yyyy p");
};

type SearchedTierProps = {
  entries: ProvenanceSearchedEntry[];
  /** Cited/read passages, used to attribute a per-collection result summary. */
  citedSources?: ProvenanceSource[];
  readSources?: ProvenanceSource[];
};

/**
 * Result summary for a collection (spec §5.1 / mockup): "N passages from M
 * documents. X cited, Y read only." Omitted for collections with no attributed
 * passages (e.g. an unnamed general search).
 */
const resultSummary = (g: SearchedCollectionGroup): string | null => {
  const passages = g.passagesCited + g.passagesRead;
  if (passages === 0) return null;
  const docs = g.documentsFromPassages || passages;
  const head = `${passages} passage${passages === 1 ? "" : "s"} from ${docs} document${
    docs === 1 ? "" : "s"
  }`;
  const tail: string[] = [];
  if (g.passagesCited) tail.push(`${g.passagesCited} cited`);
  if (g.passagesRead) tail.push(`${g.passagesRead} read only`);
  return `${head}. ${tail.join(", ")}.`;
};

/**
 * Searched tier — one card per collection/dataset queried (spec §5.1). The
 * collapsed card shows the collection name, document count, and querying agent;
 * expanding reveals the actual queries run and a cited/read result summary.
 *
 * NOTE: ``tool_name`` is internal-only and is never rendered here.
 */
const SearchedTier = ({
  entries,
  citedSources = [],
  readSources = [],
}: SearchedTierProps) => {
  const theme = useTheme();
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  if (!entries.length) {
    return (
      <NoContentCard
        title="Nothing searched"
        description="No searches were run for this response."
      />
    );
  }

  const groups = groupSearchedByCollection(entries, citedSources, readSources);
  const toggle = (key: string) =>
    setExpanded((p) => ({ ...p, [key]: !p[key] }));

  return (
    <Box data-testid="aa-src-searched-list">
      {groups.map((g, index) => {
        const open = !!expanded[g.key];
        const summary = resultSummary(g);
        const title = g.name ?? "General search";
        return (
          <Box
            key={g.key}
            data-testid={`aa-src-searched-item-${index}`}
            sx={{
              borderRadius: 1,
              m: 1,
              border: `1px solid ${theme.palette.divider}`,
              overflow: "hidden",
            }}
          >
            <Box
              role="button"
              tabIndex={0}
              aria-expanded={open}
              onClick={() => toggle(g.key)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  toggle(g.key);
                }
              }}
              sx={{
                p: 1.5,
                cursor: "pointer",
                "&:hover": { backgroundColor: "action.hover" },
              }}
            >
              <Stack direction="row" spacing={1} alignItems="center">
                <TravelExploreOutlined
                  sx={{ fontSize: "1.1rem", color: "text.secondary" }}
                />
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 600, flex: 1, minWidth: 0 }}
                  noWrap
                  title={title}
                >
                  {title}
                </Typography>
                <AgentBadge agent={g.agent} />
                {open ? (
                  <ExpandLess
                    sx={{ fontSize: "1.1rem", color: "text.secondary" }}
                  />
                ) : (
                  <ExpandMore
                    sx={{ fontSize: "1.1rem", color: "text.secondary" }}
                  />
                )}
              </Stack>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", mt: 0.5 }}
                data-testid={`aa-src-searched-detail-${index}`}
              >
                {g.documentCount != null && (
                  <>
                    {g.documentCount.toLocaleString()} document
                    {g.documentCount === 1 ? "" : "s"} ·{" "}
                  </>
                )}
                {g.queries.length} quer{g.queries.length === 1 ? "y" : "ies"}
              </Typography>
            </Box>

            <Collapse in={open} unmountOnExit>
              <Box sx={{ px: 1.5, pb: 1.5 }}>
                <Typography
                  variant="caption"
                  sx={{
                    display: "block",
                    fontWeight: 600,
                    color: "text.secondary",
                    textTransform: "uppercase",
                    letterSpacing: "0.06em",
                    fontSize: "0.625rem",
                    mb: 0.5,
                  }}
                >
                  How it was searched
                </Typography>
                <Stack spacing={0.5} sx={{ mb: summary ? 1.5 : 0 }}>
                  {g.queries.map((q, qi) => (
                    <Stack
                      key={qi}
                      direction="row"
                      spacing={0.5}
                      alignItems="center"
                      data-testid={`aa-src-searched-query-${index}-${qi}`}
                    >
                      <SearchOutlined
                        sx={{ fontSize: "0.85rem", color: "text.disabled" }}
                      />
                      <Typography
                        variant="caption"
                        sx={{ flex: 1, minWidth: 0 }}
                        noWrap
                        title={q.text ?? undefined}
                      >
                        {q.text || "(no query text)"}
                      </Typography>
                      {formatTimestamp(q.timestamp) && (
                        <Typography
                          variant="caption"
                          color="text.disabled"
                          sx={{ flexShrink: 0 }}
                        >
                          {formatTimestamp(q.timestamp)}
                        </Typography>
                      )}
                    </Stack>
                  ))}
                </Stack>

                {summary && (
                  <>
                    <Typography
                      variant="caption"
                      sx={{
                        display: "block",
                        fontWeight: 600,
                        color: "text.secondary",
                        textTransform: "uppercase",
                        letterSpacing: "0.06em",
                        fontSize: "0.625rem",
                        mb: 0.5,
                      }}
                    >
                      Results
                    </Typography>
                    <Chip
                      label={summary}
                      size="small"
                      variant="outlined"
                      data-testid={`aa-src-searched-results-${index}`}
                      sx={{
                        height: "auto",
                        py: 0.25,
                        fontSize: "0.7rem",
                        borderColor: "divider",
                        "& .MuiChip-label": { whiteSpace: "normal" },
                      }}
                    />
                  </>
                )}
              </Box>
            </Collapse>
          </Box>
        );
      })}
    </Box>
  );
};

export default SearchedTier;
